/*
 * Product groups whose customer-platform bundle card is presented by the hubs
 * it bundles (matching the in-app cards): the /crm Free Tools page (FREE) and
 * the /suite Customer Platform page (PLATFORM). On every other page (the hub
 * pages) the same bundle card shows its priority features instead.
 */
const BUNDLED_HUBS_PRODUCT_GROUPS = new Set(['FREE', 'PLATFORM']);
function extractTermPricing(purchaseOptions, selectedTerm) {
  if (typeof selectedTerm !== 'string') return undefined;
  const option = purchaseOptions[selectedTerm];
  if (!option) return undefined;
  return {
    monthlyPrice: option.monthlyPrice,
    listPrice: option.listPrice,
    discountCount: option.discountCount,
    productPrices: option.productPrices
  };
}
function resolveFeatureApiName(cardProductLevel, response, fallback) {
  var _response$featureProd, _response$products, _products$fallback;
  if (typeof cardProductLevel !== 'string') return fallback;
  const featureProducts = (_response$featureProd = response.featureProducts) !== null && _response$featureProd !== void 0 ? _response$featureProd : [];
  const products = (_response$products = response.products) !== null && _response$products !== void 0 ? _response$products : {};
  if (featureProducts.includes(fallback) && ((_products$fallback = products[fallback]) === null || _products$fallback === void 0 ? void 0 : _products$fallback.productLevel) === cardProductLevel) {
    return fallback;
  }
  const match = featureProducts.find(apiName => {
    var _products$apiName;
    return ((_products$apiName = products[apiName]) === null || _products$apiName === void 0 ? void 0 : _products$apiName.productLevel) === cardProductLevel;
  });
  return match !== null && match !== void 0 ? match : fallback;
}
function buildCardData(productApiName, order, response, selectedTerm, productGroup) {
  var _productPricing$purch, _featureApiName, _product$bundledProdu, _productApiName, _product$quantifiable, _product$packagedQuan, _product$quantityPack;
  const {
    products,
    pricing,
    priorityFeatures,
    purchaseMotions
  } = response;
  const product = (products !== null && products !== void 0 ? products : {})[productApiName];
  const productPricing = (pricing !== null && pricing !== void 0 ? pricing : {})[productApiName];
  if (!product) return undefined;
  const availableTerms = (_productPricing$purch = productPricing === null || productPricing === void 0 ? void 0 : productPricing.purchaseOptions) !== null && _productPricing$purch !== void 0 ? _productPricing$purch : {};
  const featureApiName = resolveFeatureApiName(product.productLevel, response, productApiName);
  const features = (_featureApiName = (priorityFeatures !== null && priorityFeatures !== void 0 ? priorityFeatures : {})[featureApiName]) !== null && _featureApiName !== void 0 ? _featureApiName : [];
  const bundled = (_product$bundledProdu = product.bundledProducts) !== null && _product$bundledProdu !== void 0 ? _product$bundledProdu : [];

  /*
   * On the /crm and /suite pages the customer-platform bundle card is shown by
   * the hubs it bundles; on the hub pages the same bundle card shows its
   * priority features, falling back to the bundled hubs only when it has no
   * features to surface.
   */
  const showsBundledHubsForGroup = productGroup !== undefined && BUNDLED_HUBS_PRODUCT_GROUPS.has(productGroup);
  const showBundledHubs = bundled.length > 0 && (showsBundledHubsForGroup || features.length === 0);
  let featureDisplay;
  if (product.productLevel === 'FREE') {
    featureDisplay = {
      type: 'free'
    };
  } else if (showBundledHubs) {
    featureDisplay = {
      type: 'bundledHubs',
      bundledProducts: bundled
    };
  } else {
    featureDisplay = {
      type: 'priority',
      features
    };
  }
  return {
    productApiName,
    order,
    displayName: product.displayName,
    productLevel: product.productLevel,
    hasBundledProducts: bundled.length > 0,
    selectedTermPricing: extractTermPricing(availableTerms, selectedTerm),
    availableTerms,
    purchaseMotions: (_productApiName = (purchaseMotions !== null && purchaseMotions !== void 0 ? purchaseMotions : {})[productApiName]) !== null && _productApiName !== void 0 ? _productApiName : [],
    totalPackagedSeats: product.totalPackagedSeats,
    quantifiable: (_product$quantifiable = product.quantifiable) !== null && _product$quantifiable !== void 0 ? _product$quantifiable : false,
    packagedQuantitiesMap: (_product$packagedQuan = product.packagedQuantitiesMap) !== null && _product$packagedQuan !== void 0 ? _product$packagedQuan : {},
    quantityPacks: (_product$quantityPack = product.quantityPacks) !== null && _product$quantityPack !== void 0 ? _product$quantityPack : [],
    requiredOnboarding: productPricing === null || productPricing === void 0 ? void 0 : productPricing.requiredOnboarding,
    featureDisplay
  };
}
export function mapToPresentationalCards(response, selectedTerm, productGroup) {
  var _response$merchCards;
  const sortedMerchCards = [...((_response$merchCards = response.merchCards) !== null && _response$merchCards !== void 0 ? _response$merchCards : [])].sort((a, b) => a.order - b.order);
  return sortedMerchCards.reduce((cards, merchCard) => {
    const card = buildCardData(merchCard.productApiName, merchCard.order, response, selectedTerm, productGroup);
    if (card) cards.push(card);
    return cards;
  }, []);
}
export function mapToSchemaProducts(response, selectedTerm, productGroup) {
  var _response$schemaProdu;
  return ((_response$schemaProdu = response.schemaProducts) !== null && _response$schemaProdu !== void 0 ? _response$schemaProdu : []).reduce((result, productApiName, index) => {
    const card = buildCardData(productApiName, index, response, selectedTerm, productGroup);
    if (card) result.push(card);
    return result;
  }, []);
}
export function mapToStandaloneProducts(response, selectedTerm, productGroup) {
  const {
    products,
    merchCards
  } = response;
  const merchCardApiNames = new Set((merchCards !== null && merchCards !== void 0 ? merchCards : []).map(mc => mc.productApiName));
  return Object.keys(products !== null && products !== void 0 ? products : {}).reduce((result, productApiName, index) => {
    if (merchCardApiNames.has(productApiName)) return result;
    const card = buildCardData(productApiName, index, response, selectedTerm, productGroup);
    if (card) result.push(card);
    return result;
  }, []);
}